/**
 * `requirementCheck.ts` — checks a measured part against a requirement read out
 * of a document, for `Get_Part_Dimensions`' `requirement*` arguments.
 *
 * The split it enforces: the MODEL reads the passage and states the requirement
 * as numbers, unit and datum; THIS FILE converts units and decides the verdict.
 * Spark got the comparison right by mental arithmetic in the 2026-10-29 runs
 * (184.08 cm against 1525 mm ± 50 mm), but inches against centimetres, a
 * one-sided limit, or a figure on the boundary is where that fails — so no part
 * of the verdict is left to it.
 *
 * Three guards keep the requirement honest before anything is compared (the
 * third, {@link verifyRequirementSubject}, checks it is about THIS part), and
 * {@link decideAcrossDocuments} then weighs it against what else the search returned:
 *   - the QUOTE must appear in a passage a document search actually returned
 *     (normalised for spacing, case and the ± spellings), so the requirement is
 *     the document's, not one the model remembered or composed;
 *   - every number given (min / max / nominal / tolerance) must appear in that
 *     quote, so the figures were read from it rather than added to it.
 *
 * Pure: no IOC, no DOM, no Three. Everything is in metres internally and the
 * caller passes the measured figures unrounded.
 */

export type HeightLine = "bottomline" | "centerline" | "topline";
export const K_HEIGHT_LINES: readonly HeightLine[] = ["bottomline", "centerline", "topline"];

export interface RequirementInput {
  readonly quote: string;
  /** The words in the quote naming what the requirement is about — see {@link verifyRequirementSubject}. */
  readonly subject: string | null;
  readonly min: number | null;
  readonly max: number | null;
  readonly nominal: number | null;
  readonly tolerance: number | null;
  readonly unit: string;
  /** The line the document measures to, or null when it does not say. */
  readonly datum: HeightLine | null;
}

export type LineVerdict = "within" | "above" | "below" | "at_limit";

export type RequirementVerdict =
  /** Inside the requirement for every line that was checked. */
  | "within"
  /** Outside it for every line that was checked. */
  | "outside"
  /** The document names no datum and the lines disagree: the verdict depends on how it is measured. */
  | "depends_on_datum"
  /** A checked line sits within rounding of a bound, so no verdict is safe. */
  | "at_limit";

export interface RequirementResult {
  readonly verdict: RequirementVerdict;
  /** The figures the verdict covers: the named datum, or all of them when none is named. */
  readonly linesChecked: readonly string[];
  readonly perLine: Readonly<Record<string, LineVerdict>>;
  /** The bounds in metres; null for an open side. */
  readonly lowerM: number | null;
  readonly upperM: number | null;
  /** The factor from the requirement's unit to metres. */
  readonly unitToMetres: number;
  /** The requirement's unit as the document wrote it, normalised to its short form. */
  readonly unitLabel: string;
}

/**
 * Below this a measured line counts as ON the bound: half a millimetre, finer
 * than any figure a mounting-height spec states and coarser than the rounding
 * of the spoken figures (F2 in the display unit). A verdict that would flip on
 * rounding is reported as `at_limit` instead of guessed.
 */
export const K_AT_LIMIT_M = 0.0005;

const K_UNITS: ReadonlyArray<{ readonly label: string; readonly metres: number; readonly names: readonly string[] }> = [
  { label: "mm", metres: 0.001, names: ["mm", "millimeter", "millimeters", "millimetre", "millimetres"] },
  { label: "cm", metres: 0.01, names: ["cm", "centimeter", "centimeters", "centimetre", "centimetres"] },
  { label: "m", metres: 1, names: ["m", "meter", "meters", "metre", "metres"] },
  { label: "in", metres: 0.0254, names: ["in", "in.", "inch", "inches", "\"", "”"] },
  { label: "ft", metres: 0.3048, names: ["ft", "ft.", "foot", "feet", "'", "’"] },
];

export function parseLengthUnit(unit: string): { label: string; metres: number } | null {
  const key = unit.trim().toLowerCase();
  for (const u of K_UNITS) if (u.names.includes(key)) return { label: u.label, metres: u.metres };
  return null;
}

/**
 * Lower-cases, folds the ways a document and a model spell the same thing (±
 * as "+/-" or "plus or minus", typographic quotes and dashes, non-breaking
 * spaces), and collapses whitespace — so a quote matches its passage however
 * the PDF extraction laid it out.
 */
export function normalizeForQuoteMatch(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\u00a0\u2007\u202f]/g, " ")
    .replace(/[\u2018\u2019\u2032]/g, "'")
    .replace(/[\u201c\u201d\u2033]/g, "\"")
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/plus\s+or\s+minus|\+\s*\/\s*-|\+\s*-(?=\s*\d)/g, "±")
    .replace(/\s+/g, " ")
    .trim();
}

/** Every number written in the text, commas between digit groups removed. */
export function numbersIn(text: string): number[] {
  const out: number[] = [];
  for (const m of text.replace(/(\d),(?=\d{3}\b)/g, "$1").matchAll(/\d+(?:\.\d+)?/g)) out.push(Number(m[0]));
  return out;
}

/**
 * `null` when the quote is genuinely in one of the passages and carries every
 * number given; otherwise the reason, as the tool error the agent receives.
 */
export function verifyRequirementQuote(
  input: RequirementInput,
  passages: readonly string[],
): string | null {
  const quote = normalizeForQuoteMatch(input.quote);
  if (quote.length < 8) return "error: requirement_quote_missing. Pass requirementQuote: the requirement's exact words from the passage.";
  const found = passages.some((p) => normalizeForQuoteMatch(p).includes(quote));
  if (!found) {
    return "error: requirement_not_in_search_result. requirementQuote is not in any passage a document search returned. "
      + "Quote the passage word for word, or, when no passage states a requirement for this part, give the Not found reply.";
  }
  const inQuote = numbersIn(input.quote);
  for (const [name, value] of [
    ["requirementMin", input.min],
    ["requirementMax", input.max],
    ["requirementNominal", input.nominal],
    ["requirementTolerance", input.tolerance],
  ] as const) {
    if (value == null) continue;
    if (!inQuote.some((n) => Math.abs(n - value) < 1e-9)) {
      return `error: requirement_number_not_in_quote. ${name}=${value} does not appear in requirementQuote. Take every figure from the quote as written, in the quote's own unit.`;
    }
  }
  return null;
}

/** Words a part name or a subject carries that say nothing about what the thing is. */
const K_SUBJECT_STOPWORDS = new Set([
  "a", "an", "and", "the", "of", "for", "to", "in", "on", "at", "or", "with", "by", "from", "all", "any", "each",
]);

/**
 * The meaningful words of a name: lower-cased, split on everything that is not
 * a letter or digit, and stripped of part-number codes (any token with a digit
 * in it, such as `u390754`) and of single letters — a CAD name is mostly those,
 * and they never name what a requirement is about.
 */
export function subjectWords(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 2 && !/\d/.test(w) && !K_SUBJECT_STOPWORDS.has(w));
}

/**
 * `null` when the requirement is about THIS part; otherwise the reason.
 *
 * The quote guard proves the words are the document's, and nothing more: on
 * 2026-10-29 a real quote about barrier edges ("must not exceed 37 inches or
 * 940 mm") was checked against an AGV and came back a confident "No". So the
 * agent also names what the requirement is ABOUT, in the quote's own words
 * (`requirementSubject`), and that has to share a word with the part's name or
 * one of its aliases — an exact word, or its plural. The alias is the link by
 * design: a customer tells Spark a CAD part is an "HMI" by giving it that alias,
 * and a document that says "displays and HMIs" then reaches it. A part with no
 * such word is NOT checked, which is the refusal the user gets.
 */
export function verifyRequirementSubject(
  subject: string | null,
  quote: string,
  partNames: readonly string[],
): string | null {
  if (!subject || subject.trim().length === 0) {
    return "error: requirement_subject_missing. Pass requirementSubject: the words in the quote that name what the requirement is about.";
  }
  if (!normalizeForQuoteMatch(quote).includes(normalizeForQuoteMatch(subject))) {
    return "error: requirement_subject_not_in_quote. requirementSubject must be words from requirementQuote.";
  }
  const partWords = new Set(partNames.flatMap(subjectWords));
  const matches = subjectWords(subject).some(
    (w) => partWords.has(w) || (w.endsWith("es") && partWords.has(w.slice(0, -2))) || (w.endsWith("s") && partWords.has(w.slice(0, -1))),
  );
  if (!matches) {
    return `error: requirement_not_about_this_part. The requirement is about '${subject.trim()}', and that is not this part's name or any of its aliases, so it is not this part's requirement. Give the Not found reply.`;
  }
  return null;
}

/** Bounds in the requirement's own unit, or the reason none can be formed. */
export function requirementBounds(
  input: RequirementInput,
): { lower: number | null; upper: number | null } | string {
  if (input.nominal != null) {
    if (input.tolerance == null) {
      return "error: requirement_has_no_tolerance. A single nominal figure with no tolerance cannot be met or missed by a measurement; answer that it cannot be told.";
    }
    return { lower: input.nominal - input.tolerance, upper: input.nominal + input.tolerance };
  }
  if (input.min == null && input.max == null) {
    return "error: requirement_bounds_missing. Pass requirementMin and/or requirementMax, or requirementNominal with requirementTolerance.";
  }
  if (input.min != null && input.max != null && input.min > input.max) {
    return "error: requirement_bounds_inverted. requirementMin is larger than requirementMax.";
  }
  return { lower: input.min, upper: input.max };
}

function lineVerdict(valueM: number, lowerM: number | null, upperM: number | null): LineVerdict {
  if (lowerM != null) {
    if (valueM < lowerM - K_AT_LIMIT_M) return "below";
    if (valueM <= lowerM + K_AT_LIMIT_M) return "at_limit";
  }
  if (upperM != null) {
    if (valueM > upperM + K_AT_LIMIT_M) return "above";
    if (valueM >= upperM - K_AT_LIMIT_M) return "at_limit";
  }
  return "within";
}

/**
 * The verdict against whatever figures the property has (see
 * {@link measuredForProperty}). A named datum checks that line alone. With none
 * named, all are checked and the answer is definite only when they agree — the
 * measurement method cannot change it then (user rule, 2026-10-29).
 */
export function checkRequirement(
  input: RequirementInput,
  measured: Readonly<Record<string, number>>,
): RequirementResult | string {
  const unit = parseLengthUnit(input.unit);
  if (!unit) return `error: requirement_unit_unknown. requirementUnit '${input.unit}' is not a length unit (mm, cm, m, in or ft).`;
  const bounds = requirementBounds(input);
  if (typeof bounds === "string") return bounds;

  const lowerM = bounds.lower == null ? null : bounds.lower * unit.metres;
  const upperM = bounds.upper == null ? null : bounds.upper * unit.metres;
  const lines = Object.keys(measured);
  const perLine: Record<string, LineVerdict> = {};
  for (const line of lines) perLine[line] = lineVerdict(measured[line], lowerM, upperM);
  const linesChecked = input.datum && input.datum in measured ? [input.datum] : lines;
  const verdicts = linesChecked.map((l) => perLine[l]);

  let verdict: RequirementVerdict;
  if (verdicts.includes("at_limit")) verdict = "at_limit";
  else if (verdicts.every((v) => v === "within")) verdict = "within";
  else if (verdicts.every((v) => v !== "within")) verdict = "outside";
  else verdict = "depends_on_datum";

  return { verdict, linesChecked, perLine, lowerM, upperM, unitToMetres: unit.metres, unitLabel: unit.label };
}

// ---- What a requirement is checked against --------------------------------

/**
 * Every dimension `Get_Part_Dimensions` can measure, and so every dimension a
 * requirement can be checked against. `height_above_ground` is how far up the part
 * sits; `overall_height` is how tall the part itself is. They are different
 * figures that documents both call "height".
 */
export type RequirementProperty = "height_above_ground" | "overall_height" | "width" | "depth" | "length";

export const K_REQUIREMENT_PROPERTIES: readonly RequirementProperty[] = [
  "height_above_ground",
  "overall_height",
  "width",
  "depth",
  "length",
];

/** The tool's measurement of one part, in metres. */
export interface PartMeasurements {
  readonly width: number;
  readonly height: number;
  readonly depth: number;
  readonly bottomline: number;
  readonly centerline: number;
  readonly topline: number;
}

/**
 * The measured figures a requirement on this property could be about. More than
 * one means the document does not settle which, and {@link checkRequirement} gives
 * a definite verdict only when they all agree, the rule the three height lines
 * already follow (user rule, 2026-10-29).
 *
 * "length" is the case besides height: the tool measures a part along its own axes
 * and has no axis called length, so a document's length could be either of them.
 */
export function measuredForProperty(
  property: RequirementProperty,
  part: PartMeasurements,
): Readonly<Record<string, number>> {
  switch (property) {
    case "height_above_ground":
      return { bottomline: part.bottomline, centerline: part.centerline, topline: part.topline };
    case "overall_height":
      return { height: part.height };
    case "width":
      return { width: part.width };
    case "depth":
      return { depth: part.depth };
    case "length":
      return { width: part.width, depth: part.depth };
  }
}

// ---- Competing requirements -----------------------------------------------

/**
 * Two requirements this close state the same thing: a document that gives
 * "1525 mm +/- 50 mm" and elsewhere "60 in. +/- 2 in." differs by 1.8 mm only
 * because of rounding, and that is not a disagreement.
 */
const K_SAME_REQUIREMENT_M = 0.002;

const K_BARE_WORDS = ["the", "a", "an", "its", "their"];
const K_WHOLE_PART_WORDS = [...K_BARE_WORDS, "overall", "total", "maximum", "max", "minimum", "min", "outer", "outside", "external", "nominal"];

/**
 * How a document says each property. `modifiers` are the words allowed right before
 * the noun, and they are deliberately short lists: "font height", "slot width" and
 * "clearance height" sit in the same documents, and a false disagreement costs more
 * than a missed one, which only leaves the verdict as it was.
 *
 * A bare "height" counts for both height properties, because a document does not
 * say which it means.
 */
const K_PROPERTY_WORDS: Readonly<Record<RequirementProperty, {
  readonly noun: string;
  readonly adjective: string | null;
  readonly modifiers: ReadonlySet<string>;
  readonly phrase: RegExp | null;
}>> = {
  height_above_ground: {
    noun: "height",
    adjective: null,
    modifiers: new Set([...K_BARE_WORDS, "vertical", "viewing", "mounting", "mounted", "installation", "installed", "screen", "display", "displays", "centerline", "center", "centre", "operating"]),
    phrase: /\b(?:mounted|installed|positioned|located)\s+(?:at|to)\b/i,
  },
  overall_height: { noun: "height", adjective: "tall", modifiers: new Set(K_WHOLE_PART_WORDS), phrase: null },
  width: { noun: "width", adjective: "wide", modifiers: new Set(K_WHOLE_PART_WORDS), phrase: null },
  depth: { noun: "depth", adjective: "deep", modifiers: new Set(K_WHOLE_PART_WORDS), phrase: null },
  length: { noun: "length", adjective: "long", modifiers: new Set(K_WHOLE_PART_WORDS), phrase: null },
};

const K_LENGTH = "(mm|millimet(?:er|re)s?|cm|centimet(?:er|re)s?|m|met(?:er|re)s?|in\\.?|inch(?:es)?|ft\\.?|feet|foot)(?![a-z0-9])";
const K_NUMBER = "(\\d+(?:[.,]\\d+)?)";
const K_BELOW_WORDS = "≤|<=|<|must not exceed|shall not exceed|not exceed|no more than|no higher than|not more than|at most|maximum of|maximum|max\\.?|up to";
const K_ABOVE_WORDS = "≥|>=|>|at least|no less than|no lower than|not less than|minimum of|minimum|min\\.?";

export interface RequirementPassage {
  readonly text: string;
  readonly fileName: string | null;
}

/** Whose requirement a sentence states: this part by its own name, or its kind by an alias. */
export type RequirementStanding = "part" | "kind";

export interface CompetingRequirement {
  /** The sentence, whitespace collapsed, as the document states it. */
  readonly quote: string;
  readonly fileName: string | null;
  readonly lowerM: number | null;
  readonly upperM: number | null;
  readonly datum: HeightLine | null;
  readonly standing: RequirementStanding;
}

/**
 * Sentences of a passage as PDF extraction left them. A parenthesis holding a
 * figure goes first: "1525 mm +/- 50 mm (60 in.\n +/- 2 in.)" is one requirement
 * in two units, and the "in." inside it would otherwise end the sentence.
 */
export function requirementSentences(text: string): string[] {
  return text
    .replace(/\([^()]*\d[^()]*\)/g, " ")
    .replace(/([.;:!?])\s+(?=[A-Z0-9])/g, "$1\n\n")
    .split(/\n\s*\n/)
    .map((sentence) => sentence.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

/**
 * The part of a sentence stating this property's figures, or null when the sentence
 * is not about it. After the LAST word for it, not the whole sentence: PDF extraction
 * glues a table row onto the heading after it ("6100 mm 240 in. *Typical computer
 * font/display 3.1.5.2.1 Display/HMI Viewing Height The viewing height ..." in the
 * real SP-ERGO page), so the first figure can belong to another table altogether.
 * An adjective comes after its figure ("400 mm wide"), so there it is the text before.
 */
export function propertyStatement(sentence: string, property: RequirementProperty): string | null {
  const words = K_PROPERTY_WORDS[property];
  const lower = sentence.toLowerCase();

  if (words.adjective) {
    const adjective = lower.match(new RegExp(`${K_NUMBER}\\s*${K_LENGTH}\\s+${words.adjective}\\b`));
    if (adjective?.index !== undefined) {
      return sentence.slice(Math.max(0, adjective.index - 40), adjective.index + adjective[0].length);
    }
  }
  const nouns = [...lower.matchAll(new RegExp(`(?:([a-z-]+)\\s+)?\\b${words.noun}\\b`, "g"))]
    .filter((match) => match[1] === undefined || words.modifiers.has(match[1]));
  const lastNoun = nouns[nouns.length - 1];
  if (lastNoun?.index !== undefined) return sentence.slice(lastNoun.index);
  const phrase = words.phrase ? sentence.match(words.phrase) : null;
  return phrase?.index !== undefined ? sentence.slice(phrase.index) : null;
}

/** The figures a sentence requires, in metres, or null when it states none this can read. */
export function parseRequirementBounds(
  sentence: string,
): { readonly lowerM: number | null; readonly upperM: number | null } | null {
  const text = normalizeForQuoteMatch(sentence);
  // "at least 25 mm greater than the frame" is relative to something else, and no
  // absolute requirement can be read from it. Real GCCB text, 2026-11-01.
  if (new RegExp(`${K_NUMBER}\\s*${K_LENGTH}\\s+(?:greater|less|more|wider|narrower|taller|shorter|longer|deeper|higher|lower|larger|smaller)\\s+than\\b`).test(text)) return null;
  const metres = (value: string, unit: string) => {
    const factor = parseLengthUnit(unit)?.metres;
    const figure = Number(value.replace(/,/g, ""));
    return factor != null && Number.isFinite(figure) ? figure * factor : null;
  };
  const both = (lower: number | null, upper: number | null) =>
    lower == null || upper == null ? null : { lowerM: lower, upperM: upper };

  let match = text.match(new RegExp(`${K_NUMBER}\\s*(?:${K_LENGTH})?\\s*±\\s*${K_NUMBER}\\s*${K_LENGTH}`));
  if (match) {
    const nominal = metres(match[1], match[2] ?? match[4]);
    const tolerance = metres(match[3], match[4]);
    return nominal == null || tolerance == null ? null : both(nominal - tolerance, nominal + tolerance);
  }
  match = text.match(new RegExp(`${K_NUMBER}\\s*(?:${K_LENGTH})?\\s*(?:-|to)\\s*${K_NUMBER}\\s*${K_LENGTH}`));
  if (match) return both(metres(match[1], match[2] ?? match[4]), metres(match[3], match[4]));
  match = text.match(new RegExp(`(?:${K_BELOW_WORDS})\\s*${K_NUMBER}\\s*${K_LENGTH}`));
  if (match) {
    const upper = metres(match[1], match[2]);
    return upper == null ? null : { lowerM: null, upperM: upper };
  }
  match = text.match(new RegExp(`(?:${K_ABOVE_WORDS})\\s*${K_NUMBER}\\s*${K_LENGTH}`));
  if (match) {
    const lower = metres(match[1], match[2]);
    return lower == null ? null : { lowerM: lower, upperM: null };
  }
  match = text.match(new RegExp(`${K_NUMBER}\\s*${K_LENGTH}`));
  if (match) {
    const value = metres(match[1], match[2]);
    if (value == null) return null;
    // "maximum height of 1.5 m": the limit word comes before the noun, not the figure.
    if (/^(?:maximum|max\b)/.test(text)) return { lowerM: null, upperM: value };
    if (/^(?:minimum|min\b)/.test(text)) return { lowerM: value, upperM: null };
    return { lowerM: value, upperM: value };
  }
  return null;
}

/** The line a sentence says it is measured to, when it says. */
export function parseRequirementDatum(sentence: string): HeightLine | null {
  const lower = sentence.toLowerCase();
  if (/\bcent(?:er|re)\s?line\b/.test(lower)) return "centerline";
  if (/\b(?:to|at) the top of\b/.test(lower)) return "topline";
  if (/\b(?:to|at) the (?:bottom|lowest point) of\b/.test(lower)) return "bottomline";
  return null;
}

/** The name's words all appear in the sentence, each as a word or its plural. */
function sentenceNames(sentence: string, name: string): boolean {
  const nameWords = subjectWords(name);
  if (nameWords.length === 0) return false;
  const words = new Set(subjectWords(sentence));
  return nameWords.every((word) => words.has(word) || words.has(`${word}s`) || words.has(`${word}es`));
}

/**
 * Sentences in the passages stating something different from this requirement,
 * for this part or its kind, on this property.
 *
 * A sentence must name the WHOLE of a name or alias, tighter than
 * {@link verifyRequirementSubject}'s shared word: "COGNEX VISION VIEW" shares "view"
 * with every drawing's "FRONT VIEW".
 *
 * Bounds containing the requirement's own do not compete, so "must not exceed
 * 1600 mm" stands beside "1525 mm +/- 50 mm". The reverse does: checking a part
 * against the looser of two is how a part that fails the tighter one passes.
 */
export function findCompetingRequirements(
  input: RequirementInput,
  property: RequirementProperty,
  passages: readonly RequirementPassage[],
  partNames: readonly string[],
  aliases: readonly string[],
): CompetingRequirement[] {
  const unit = parseLengthUnit(input.unit);
  const bounds = requirementBounds(input);
  if (!unit || typeof bounds === "string") return [];
  const lowerM = bounds.lower == null ? null : bounds.lower * unit.metres;
  const upperM = bounds.upper == null ? null : bounds.upper * unit.metres;

  const competing: CompetingRequirement[] = [];
  const seen = new Set<string>();
  for (const passage of passages) {
    for (const quote of requirementSentences(passage.text)) {
      const standing = standingOf(quote, partNames, aliases);
      if (!standing) continue;
      const statement = propertyStatement(quote, property);
      if (statement === null) continue;
      const figures = parseRequirementBounds(statement);
      if (!figures) continue;

      const datum = property === "height_above_ground" ? parseRequirementDatum(quote) : null;
      const lowerMatches = lowerM != null && figures.lowerM != null && Math.abs(figures.lowerM - lowerM) <= K_SAME_REQUIREMENT_M;
      const upperMatches = upperM != null && figures.upperM != null && Math.abs(figures.upperM - upperM) <= K_SAME_REQUIREMENT_M;
      const sameFigures =
        (lowerMatches || (lowerM == null && figures.lowerM == null))
        && (upperMatches || (upperM == null && figures.upperM == null));
      const containsOurs =
        (figures.lowerM == null || (lowerM != null && figures.lowerM <= lowerM + K_SAME_REQUIREMENT_M))
        && (figures.upperM == null || (upperM != null && figures.upperM >= upperM - K_SAME_REQUIREMENT_M));
      // One figure inside our bounds is the same target stated without its
      // tolerance: "should be 1525 mm" beside "1525 mm +/- 50 mm".
      const insideOurs =
        figures.lowerM != null
        && figures.lowerM === figures.upperM
        && (lowerM == null || figures.lowerM >= lowerM - K_SAME_REQUIREMENT_M)
        && (upperM == null || figures.lowerM <= upperM + K_SAME_REQUIREMENT_M);
      const datumDiffers = datum != null && input.datum != null && datum !== input.datum;
      if ((sameFigures || containsOurs || insideOurs) && !datumDiffers) continue;

      const key = `${figures.lowerM}|${figures.upperM}|${datum}`;
      if (seen.has(key)) continue;
      seen.add(key);
      competing.push({ quote, fileName: passage.fileName, lowerM: figures.lowerM, upperM: figures.upperM, datum, standing });
    }
  }
  return competing;
}

function standingOf(
  sentence: string,
  partNames: readonly string[],
  aliases: readonly string[],
): RequirementStanding | null {
  if (partNames.some((name) => sentenceNames(sentence, name))) return "part";
  if (aliases.some((alias) => sentenceNames(sentence, alias))) return "kind";
  return null;
}

/**
 * The passages of the newest search that returned this quote, which are the ones
 * that can compete with it. The whole window of recent searches would let an
 * earlier question's passages disagree with this one's. Empty when no search holds
 * it, which {@link verifyRequirementQuote} has already refused.
 */
export function passagesOfSearchHolding(
  quote: string,
  searches: ReadonlyArray<readonly RequirementPassage[]>,
): readonly RequirementPassage[] {
  const wanted = normalizeForQuoteMatch(quote);
  for (let index = searches.length - 1; index >= 0; index--) {
    if (searches[index].some((passage) => normalizeForQuoteMatch(passage.text).includes(wanted))) return searches[index];
  }
  return [];
}

/** One requirement that counted towards a {@link RequirementDecision}. */
export interface CountedRequirement {
  readonly quote: string;
  readonly fileName: string | null;
  readonly verdict: RequirementVerdict;
  /** The figures behind `verdict`: the bounds in metres and how each line compared. */
  readonly result: RequirementResult;
}

export interface RequirementDecision {
  /**
   * The answer to "does it meet the documents?". `conflicting_requirements` means
   * the requirements that count give different verdicts, so no answer is safe.
   */
  readonly verdict: RequirementVerdict | "conflicting_requirements";
  /** Every requirement behind the verdict, the agent's own first when it still counts. */
  readonly requirements: readonly CountedRequirement[];
}

/**
 * The verdict across everything the search returned, for any dimension the tool
 * measures. This is what the agent is told, in place of a verdict on its one quote.
 *
 * It exists because a prompt rule cannot hold this line. With a second, conflicting
 * requirement in the results, "do not choose between them" gave a "can't tell" in
 * 2 of 5 live runs, and the model took the first passage in the others. It reports
 * the code's verdict faithfully, though (21 of 21 correct turns).
 *
 * A sentence naming the part itself outranks those about its kind, which then do
 * not count, whichever of them the agent quoted. Of the rest:
 *  - All agree on within or outside: that is the verdict, and all are named. When
 *    no requirement the documents state would change the answer, the disagreement
 *    between them does not matter to it (user direction, 2026-11-01), the same rule
 *    {@link checkRequirement} applies across a passage's possible lines.
 *  - Otherwise `conflicting_requirements`.
 *
 * Called by `Get_Part_Dimensions`. Measured live in `docs/work-plan/M11-ai-harness-7.md`.
 */
export function decideAcrossDocuments(
  input: RequirementInput,
  property: RequirementProperty,
  passages: readonly RequirementPassage[],
  partNames: readonly string[],
  aliases: readonly string[],
  measured: Readonly<Record<string, number>>,
): RequirementDecision | string {
  const own = checkRequirement(input, measured);
  if (typeof own === "string") return own;

  const resultOf = (candidate: CompetingRequirement): RequirementResult | null => {
    const result = checkRequirement(
      {
        quote: candidate.quote,
        subject: null,
        min: candidate.lowerM,
        max: candidate.upperM,
        nominal: null,
        tolerance: null,
        unit: "m",
        datum: candidate.datum,
      },
      measured,
    );
    return typeof result === "string" ? null : result;
  };

  const competing = findCompetingRequirements(input, property, passages, partNames, aliases);
  const ownStanding = standingOf(input.quote, partNames, aliases) ?? "kind";
  const naming = competing.filter((candidate) => candidate.standing === "part");
  const outranked = ownStanding === "kind" && naming.length > 0;
  const others = outranked ? naming : competing.filter((candidate) => candidate.standing === ownStanding);

  const requirements: CountedRequirement[] = outranked
    ? []
    : [{ quote: input.quote, fileName: null, verdict: own.verdict, result: own }];
  for (const candidate of others) {
    const result = resultOf(candidate);
    if (result === null) continue;
    requirements.push({ quote: candidate.quote, fileName: candidate.fileName, verdict: result.verdict, result });
  }

  const first = requirements[0]?.verdict ?? own.verdict;
  if (requirements.length <= 1) return { verdict: first, requirements };
  const agree = requirements.every((requirement) => requirement.verdict === first);
  return { verdict: agree && (first === "within" || first === "outside") ? first : "conflicting_requirements", requirements };
}

/**
 * The decision as the agent reads it: every figure in the unit of the requirement it
 * quoted, since a competing one is read in metres and that is no unit to speak beside
 * a quote in millimetres.
 *
 * The verdict is the DECISION's. The first requirement's own figures sit beside it
 * at the top level for the single-requirement reply, and its verdict must not replace
 * this one, which is what hid `conflicting_requirements` from the agent on the first
 * live run and had it answer a conflict with a plain "Yes".
 */
export function requirementDecisionPayload(
  requirement: RequirementInput,
  property: RequirementProperty,
  decision: RequirementDecision,
  measured: Readonly<Record<string, number>>,
): Record<string, unknown> {
  const unit = parseLengthUnit(requirement.unit) ?? { label: "m", metres: 1 };
  const inUnit = (metres: number) => formatInUnit(metres, unit.metres, unit.label);
  const describe = (counted: CountedRequirement) => ({
    quote: counted.quote,
    linesChecked: counted.result.linesChecked,
    perLine: counted.result.perLine,
    lowerBound: counted.result.lowerM == null ? null : inUnit(counted.result.lowerM),
    upperBound: counted.result.upperM == null ? null : inUnit(counted.result.upperM),
  });
  const [first] = decision.requirements;
  return {
    ...(first ? describe(first) : {}),
    verdict: decision.verdict,
    property,
    // Every requirement behind the verdict, so a reply can name each one.
    requirements: decision.requirements.map((counted) => ({ ...describe(counted), verdict: counted.verdict })),
    measuredInRequirementUnit: Object.fromEntries(
      Object.entries(measured).map(([line, metres]) => [line, inUnit(metres)]),
    ),
  };
}

/** A metre figure in the requirement's unit, at a precision that can be read back against it. */
export function formatInUnit(metres: number, unitToMetres: number, unitLabel: string): string {
  const value = metres / unitToMetres;
  const decimals = unitLabel === "mm" ? 0 : unitLabel === "m" ? 3 : 1;
  return `${value.toFixed(decimals)} ${unitLabel}`;
}
